import { useContext, useEffect, useRef, useState } from 'react';
import { Animate, AuthContext, ViewContext } from 'components/lib';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { UMButton } from '@urbanmetrix/um-kit';
import { useLocation, useNavigate } from 'react-router-dom';
import { LeftDropDownArrow } from 'components/icons';
import { getCurrentSubscription, getPlans, type BillingPlan, type BillingSubscription } from 'api/planApi';
import {
  createCheckout,
  createCustomPlanCheckout,
  downgradeCustomSubscription,
  downgradeSubscription,
  unlockCustomPlan,
  upgradeCustomSubscription,
  upgradeSubscription,
} from 'api/billingApi';
import { getApiErrorMessage } from 'api/apiClient';
import { clearPendingSubscription } from '../../../lib/pending-subscription';

type BillingPlanWithChangeType = BillingPlan & {
  changeType?: 'upgrade' | 'downgrade' | null;
};

type PlanPriceSummary = {
  totalAnnual: number;
};

type SignupPlanProps = {
  isChangePlan?: boolean;
};

type PendingDowngradePlan = {
  planName: string;
  source: 'default' | 'custom';
  unlockToken?: string;
  plan?: BillingPlanWithChangeType | null;
} | null;

type PendingCustomPlan = {
  plan: BillingPlanWithChangeType | null;
  unlockToken: string | null;
  isLoading: boolean;
  isTrial: boolean;
} | null;

const TRIAL_PLANS_END: Date | null = new Date('2026-10-24T04:59:00+02:00');

const ENTITLEMENT_ORDER = [
  'observation-reports',
  'video-and-numerical-exports',
  'hasOnboarding',
  'monthly-support',
] as const;

export function SignupPlan({ isChangePlan = false }: SignupPlanProps) {
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext) as {
    notification: { show: (message: string, type?: string, autoclose?: boolean, className?: string) => void };
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingPlanName, setSubmittingPlanName] = useState<string | null>(null);
  const [plans, setPlans] = useState<BillingPlanWithChangeType[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<BillingSubscription | null>(null);
  const [isPlansLoading, setIsPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [customPlanCode, setCustomPlanCode] = useState('');
  const [pendingDowngradePlanName, setPendingDowngradePlanName] = useState<PendingDowngradePlan>(null);
  const [pendingTrialPlan, setPendingTrialPlan] = useState<BillingPlanWithChangeType | null>(null);
  const [pendingCustomPlan, setPendingCustomPlan] = useState<PendingCustomPlan>(null);
  const customPlanRequestIdRef = useRef(0);
  const isTrialSubscription = isChangePlan && currentSubscription?.billingInterval === 'month';
  const showTrialPlans = !isChangePlan && TRIAL_PLANS_END !== null && Date.now() < TRIAL_PLANS_END.getTime();
  const requestedBackTo = (location.state as { planBackTo?: string } | null)?.planBackTo;
  const backTo = isChangePlan || requestedBackTo === '/account/subscription' ? '/account/subscription' : '/dashboard';
  const activeOrganizationId = authContext.user?.session?.activeOrganizationId;
  const activeOrganizationName = authContext.user?.organizations?.find(
    (organization) => String(organization.id) === String(activeOrganizationId)
  )?.name;

  useEffect(() => {
    clearPendingSubscription();
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadPlans() {
      try {
        if (mounted) {
          setIsPlansLoading(true);
        }
        const [plansResponse, subscriptionResponse] = await Promise.all([
          getPlans(),
          getCurrentSubscription(isChangePlan ? { includeNextPlan: true } : undefined).catch(() => null),
        ]);

        if (!mounted) return;

        setPlans(Array.isArray(plansResponse) ? plansResponse : []);
        setCurrentSubscription(subscriptionResponse);
        setPlansError('');
      } catch (error) {
        console.error('Error loading billing plans:', error);

        if (!mounted) return;

        setPlans([]);
        setCurrentSubscription(null);
        setPlansError("We're experiencing server issues. Please try again later.");
      } finally {
        if (mounted) {
          setIsPlansLoading(false);
        }
      }
    }

    loadPlans();

    return () => {
      mounted = false;
    };
  }, [isChangePlan]);

  const openCheckoutModal = async (
    planName: string,
    source: 'default' | 'custom' = 'default',
    unlockToken?: string,
    resolvedPlan?: BillingPlanWithChangeType | null,
    trialPriceLookupKey?: string
  ) => {
    setIsSubmitting(true);
    setSubmittingPlanName(planName);
    setCheckoutError('');

    try {
      const targetPlan = resolvedPlan ?? plans.find((plan) => plan.name === planName) ?? null;
      const isPlanChangeAction = isChangePlan;
      const successUrl = isPlanChangeAction
        ? `${window.location.origin}/account/subscription`
        : `${window.location.origin}/invitations`;
      const cancelUrl = isPlanChangeAction
        ? `${window.location.origin}/account/change-plan`
        : `${window.location.origin}/register/plan`;

      if (
        isPlanChangeAction &&
        !isTrialSubscription &&
        source === 'custom' &&
        targetPlan?.changeType === 'downgrade' &&
        unlockToken
      ) {
        await downgradeCustomSubscription(getCheckoutLookupKey(targetPlan), unlockToken);
        viewContext.notification.show('Plan updated', 'success', true, 'toast');
        window.location.assign('/account/subscription');
        return;
      }

      if (
        isPlanChangeAction &&
        !isTrialSubscription &&
        source === 'default' &&
        targetPlan?.changeType === 'downgrade'
      ) {
        await downgradeSubscription(getCheckoutLookupKey(targetPlan));
        viewContext.notification.show('Plan updated', 'success', true, 'toast');
        window.location.assign('/account/subscription');
        return;
      }

      const response = isPlanChangeAction
        ? await createPlanChangeRedirect(targetPlan, successUrl, cancelUrl, source, unlockToken, isTrialSubscription)
        : source === 'custom' && unlockToken
          ? await createCustomPlanCheckout(
              getCheckoutLookupKey(targetPlan),
              unlockToken,
              successUrl,
              cancelUrl,
              true,
              trialPriceLookupKey
            )
          : await createCheckout(getCheckoutLookupKey(targetPlan), successUrl, cancelUrl, true);

      if (!response?.url) {
        setCheckoutError('Unable to initialize checkout right now.');
        return;
      }

      window.location.assign(response.url);
    } catch (error) {
      console.error('Error creating checkout:', error);

      if (isChangePlan) {
        const targetPlan = plans.find((plan) => plan.name === planName) || null;
        if (!isTrialSubscription && targetPlan?.changeType === 'downgrade') {
          viewContext.notification.show(
            getApiErrorMessage(error, 'Unable to update plan right now.'),
            'error',
            true,
            'toast'
          );
          return;
        }
      }

      if (source === 'custom') {
        viewContext.notification.show('The plan code you entered is not valid.', 'error', true, 'toast');
      } else {
        setCheckoutError('Unable to initialize checkout right now.');
      }
    } finally {
      setIsSubmitting(false);
      setSubmittingPlanName(null);
    }
  };

  const handlePlanAction = (plan: BillingPlanWithChangeType) => {
    if (isChangePlan && !isTrialSubscription && plan.changeType === 'downgrade') {
      setPendingDowngradePlanName({ planName: plan.name, source: 'default', plan });
      return;
    }

    openCheckoutModal(plan.name, 'default', undefined, plan);
  };

  const handleCustomPlanAction = async () => {
    if (!trimmedCustomPlanCode || isSubmitting) return;
    setIsSubmitting(true);
    setSubmittingPlanName(trimmedCustomPlanCode);
    setCheckoutError('');
    const requestId = customPlanRequestIdRef.current + 1;
    customPlanRequestIdRef.current = requestId;
    setPendingCustomPlan({
      plan: null,
      unlockToken: null,
      isLoading: true,
      isTrial: trimmedCustomPlanCode.startsWith('trial_'),
    });
    let shouldResetSubmittingState = true;

    try {
      const response = await unlockCustomPlan(trimmedCustomPlanCode);
      if (customPlanRequestIdRef.current !== requestId) {
        shouldResetSubmittingState = false;
        return;
      }
      setPendingCustomPlan({
        plan: response.plan,
        unlockToken: response.unlockToken,
        isLoading: false,
        isTrial: trimmedCustomPlanCode.startsWith('trial_'),
      });
    } catch (error) {
      if (customPlanRequestIdRef.current !== requestId) {
        shouldResetSubmittingState = false;
        return;
      }
      setPendingCustomPlan(null);
      console.error('Error unlocking custom plan:', error);

      if ((error as { status?: number } | null)?.status === 404) {
        viewContext.notification.show('The plan code you entered is not valid.', 'error', true, 'toast');
        return;
      }

      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to unlock the custom plan right now.'),
        'error',
        true,
        'toast'
      );
    } finally {
      if (shouldResetSubmittingState) {
        setIsSubmitting(false);
        setSubmittingPlanName(null);
      }
    }
  };

  const closeDowngradeDialog = () => {
    if (isSubmitting) return;
    setPendingDowngradePlanName(null);
  };

  const confirmDowngradePlan = async () => {
    if (!pendingDowngradePlanName) return;
    const pendingDowngrade = pendingDowngradePlanName;
    setPendingDowngradePlanName(null);
    await openCheckoutModal(
      pendingDowngrade.planName,
      pendingDowngrade.source,
      pendingDowngrade.unlockToken,
      pendingDowngrade.plan
    );
  };

  const confirmTrialPlan = async () => {
    if (!pendingTrialPlan) return;

    const plan = pendingTrialPlan;
    setIsSubmitting(true);
    setCheckoutError('');

    try {
      const response = await createCheckout(
        getCheckoutLookupKey(plan),
        `${window.location.origin}/invitations`,
        `${window.location.origin}/register/plan`,
        true,
        getTrialLookupKey(plan)
      );

      if (!response?.url) {
        setCheckoutError('Unable to initialize checkout right now.');
        return;
      }

      window.location.assign(response.url);
    } catch (error) {
      console.error('Error creating trial checkout:', error);
      setCheckoutError('Unable to initialize checkout right now.');
    } finally {
      setIsSubmitting(false);
      setPendingTrialPlan(null);
    }
  };

  const closeCustomPlanDialog = () => {
    if (isSubmitting && !pendingCustomPlan?.isLoading) return;
    customPlanRequestIdRef.current += 1;
    setPendingCustomPlan(null);
  };

  const confirmCustomPlanAction = async () => {
    if (!pendingCustomPlan || pendingCustomPlan.isLoading || !pendingCustomPlan.plan || !pendingCustomPlan.unlockToken)
      return;

    if (isChangePlan && !isTrialSubscription && pendingCustomPlan.plan.changeType === 'downgrade') {
      setPendingCustomPlan(null);
      setPendingDowngradePlanName({
        planName: pendingCustomPlan.plan.name,
        source: 'custom',
        unlockToken: pendingCustomPlan.unlockToken,
        plan: pendingCustomPlan.plan,
      });
      return;
    }

    const nextPendingPlan = pendingCustomPlan;
    const nextPlan = nextPendingPlan.plan!;
    const nextUnlockToken = nextPendingPlan.unlockToken;
    await openCheckoutModal(
      nextPlan.name,
      'custom',
      nextUnlockToken ?? undefined,
      nextPlan,
      nextPendingPlan.isTrial ? getTrialLookupKey(nextPlan) : undefined
    );
  };

  const expiredSubscriptionMessage = getExpiredSubscriptionMessage(currentSubscription);
  const trimmedCustomPlanCode = customPlanCode.trim();
  const pendingCustomPlanPrice = pendingCustomPlan?.plan ? getPlanPrice(pendingCustomPlan.plan) : null;

  return (
    <>
      <Animate type="pop" customClass="plan-shell">
        <div className="signup-plan-page-shell">
          <div className="signup-plan-page-panel">
            <div className="signup-plan-page-topbar signup-plan-page-topbar--plans">
              <button
                className="signup-plan-page-logout signup-plan-page-logout--left"
                onClick={() => navigate(backTo)}
                type="button"
                title="Back"
                aria-label="Back"
              >
                <LeftDropDownArrow size={18} />
                <span>Back</span>
              </button>
              <div className="signup-plan-page-heading">
                <Typography
                  className="signup-plan-page-title"
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    margin: 0,
                    color: 'black',
                    fontSize: '1.8rem !important',
                    lineHeight: 1.1,
                  }}
                >
                  {isChangePlan ? 'Update your plan' : 'Choose your plan'}
                </Typography>
                {showTrialPlans ? (
                  <Typography className="signup-plan-page-offer" variant="body2">
                    Special A@W offer valid until {formatTrialPlansEnd(TRIAL_PLANS_END)} for one month trial
                  </Typography>
                ) : null}
              </div>
              <div className="signup-plan-page-topbar__actions">
                <div className="signup-plan-page-user-switch__text">
                  {authContext.user?.name} - {activeOrganizationName || 'Organization'}
                </div>
              </div>
            </div>

            {expiredSubscriptionMessage ? (
              <Typography
                variant="body1"
                sx={{
                  color: '#7a2845',
                  margin: '0 0 18px',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                {expiredSubscriptionMessage}
              </Typography>
            ) : null}

            {isPlansLoading ? (
              <div style={{ minHeight: '420px', display: 'grid', placeItems: 'center' }}>
                <CircularProgress size={32} sx={{ color: '#111827' }} />
              </div>
            ) : plansError ? (
              <div role="alert" style={{ minHeight: '420px', display: 'grid', placeItems: 'center' }}>
                <Typography variant="body1" sx={{ color: '#b42318', textAlign: 'center' }}>
                  {plansError}
                </Typography>
              </div>
            ) : (
              <>
                <Grid container spacing={2}>
                  {plans.map((plan) => {
                    const planPrice = getPlanPrice(plan);

                    return (
                      <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={plan.name}>
                        <div className="signup-plan-page-plan-column">
                          <div className="signup-plan-page-card">
                            <div>
                              <div
                                className={`signup-plan-page-card-header${isChangePlan ? ' signup-plan-page-card-header--change-plan' : !showTrialPlans ? ' signup-plan-page-card-header--compact' : ''}`}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontSize: '1.8rem !important',
                                    fontWeight: 700,
                                    lineHeight: 1.05,
                                  }}
                                >
                                  {plan.productName || '—'}
                                </Typography>
                                <div
                                  className={`signup-plan-page-price-block${isChangePlan ? ' signup-plan-page-price-block--change-plan' : ''}`}
                                >
                                  {showTrialPlans && plan.trial?.lookupKey ? (
                                    <>
                                      <Typography className="signup-plan-page-offer-label" variant="body2">
                                        Special A@W Offer
                                      </Typography>
                                      <Typography className="signup-plan-page-trial-price" variant="body1">
                                        <strong>{formatCurrencyCompact(getTrialPrice(plan))}</strong>{' '}
                                        <span>{formatPlanCurrency(plan.trial.currency)}/1-month</span>
                                      </Typography>
                                      <Typography className="signup-plan-page-then" variant="body2">
                                        then
                                      </Typography>
                                    </>
                                  ) : null}
                                  <Typography variant="body1" sx={{ fontWeight: 400, margin: 0, lineHeight: 1.05 }}>
                                    <span
                                      className={`signup-plan-page-annual-price${isChangePlan || !showTrialPlans ? ' signup-plan-page-annual-price--change-plan' : ''}`}
                                    >
                                      {formatCurrencyCompact(planPrice.totalAnnual)}
                                    </span>{' '}
                                    <span className="signup-plan-page-annual-suffix">
                                      {formatPlanCurrency(plan.yearly?.currency)}/year
                                    </span>
                                  </Typography>
                                </div>
                              </div>

                              <div className="signup-plan-page-facts">
                                <div className="signup-plan-page-upper">
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'black', marginTop: '10px', lineHeight: 1.25, fontWeight: 300 }}
                                  >
                                    {plan.productDescription}
                                  </Typography>

                                  <div className="signup-plan-page-sensor-row">
                                    <strong>{formatLimitValue('volume', getPlanVolumeLimit(plan))}</strong>
                                    <span>m³/max per site</span>
                                  </div>
                                </div>

                                <div className="signup-plan-page-divider" />

                                <div className="signup-plan-page-features">
                                  {getPlanFeatureRows(plan).map((detail) => (
                                    <div className="signup-plan-page-detail-row" key={detail.label}>
                                      <strong>{detail.value}</strong>
                                      {detail.label === 'Core Features' ? (
                                        <a
                                          className="signup-plan-page-feature-link"
                                          href="https://urbanmetrix.com/product#feature-list"
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          {detail.label}
                                        </a>
                                      ) : (
                                        <span>{detail.label}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div className="signup-plan-page-cta">
                              {showTrialPlans && plan.trial?.lookupKey ? (
                                <UMButton
                                  text="Try for one month"
                                  fullWidth
                                  className="signup-plan-page-cta-button"
                                  onClick={() => setPendingTrialPlan(plan)}
                                  disabled={isSubmitting}
                                />
                              ) : null}
                              <UMButton
                                text={getCustomPlanActionLabel(plan, isChangePlan, isTrialSubscription)}
                                fullWidth
                                customClassName={`signup-plan-page-cta-button${showTrialPlans || isChangePlan ? ' signup-plan-page-cta-button--primary' : ''}`}
                                onClick={() => handlePlanAction(plan)}
                                disabled={
                                  isSubmitting ||
                                  (isChangePlan &&
                                    !isTrialSubscription &&
                                    (plan.changeType === null || plan.changeType === undefined))
                                }
                                loading={submittingPlanName === plan.name}
                              />
                            </div>
                          </div>
                        </div>
                      </Grid>
                    );
                  })}
                  <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <div className="signup-plan-page-plan-column">
                      <div className="signup-plan-page-card">
                        <div
                          className={`signup-plan-page-card-header${isChangePlan ? ' signup-plan-page-card-header--change-plan' : !showTrialPlans ? ' signup-plan-page-card-header--compact' : ''}`}
                        >
                          <Typography
                            variant="h6"
                            sx={{ fontSize: '1.8rem !important', fontWeight: 700, lineHeight: 1.05 }}
                          >
                            Enterprise
                          </Typography>
                          <Typography className="signup-plan-page-enterprise-offer" variant="body2">
                            Larger project?{' '}
                            <a href="https://urbanmetrix.com/about#contact" target="_blank" rel="noreferrer">
                              Contact us
                            </a>
                            {showTrialPlans ? ' for a Custom A@W Offer' : null}
                          </Typography>
                        </div>
                        <div className="signup-plan-page-facts">
                          <div className="signup-plan-page-upper">
                            <Typography
                              variant="body2"
                              sx={{ color: 'black', marginTop: '10px', lineHeight: 1.25, fontWeight: 300 }}
                            >
                              For projects beyond the limits of our standard plans.
                            </Typography>
                            <div className="signup-plan-page-sensor-row">
                              <strong>Custom</strong>
                              <span>m³/max per site</span>
                            </div>
                          </div>
                          <div className="signup-plan-page-divider" />
                          <div className="signup-plan-page-features">
                            {[
                              'Flexible yearly capacity',
                              'Custom storage size',
                              'Custom user number',
                              'Complete features',
                              'Team onboarding',
                              'Custom support',
                            ].map((feature) => (
                              <div className="signup-plan-page-detail-row" key={feature}>
                                <strong>✓</strong>
                                <span>{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="signup-plan-page-cta">
                          <UMButton
                            text="Contact us"
                            fullWidth
                            customClassName={`signup-plan-page-cta-button${showTrialPlans || isChangePlan ? ' signup-plan-page-cta-button--contact' : ''}`}
                            onClick={() =>
                              window.open('https://urbanmetrix.com/about#contact', '_blank', 'noopener,noreferrer')
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </Grid>
                </Grid>

                <div className="signup-plan-page-custom-section">
                  <div className="signup-plan-page-custom-row">
                    <Typography
                      variant="body1"
                      className="signup-plan-page-custom-label"
                      sx={{ color: '#111827', fontWeight: 500 }}
                    >
                      Do you have a code for a custom plan?
                    </Typography>
                    <TextField
                      value={customPlanCode}
                      onChange={(event) => {
                        setCustomPlanCode(event.target.value);
                        if (checkoutError) {
                          setCheckoutError('');
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && trimmedCustomPlanCode && !isSubmitting) {
                          event.preventDefault();
                          handleCustomPlanAction();
                        }
                      }}
                      size="small"
                      className="signup-plan-page-custom-input"
                      name="custom-plan-code-search"
                      type="search"
                      autoComplete="new-password"
                      slotProps={{
                        htmlInput: {
                          autoComplete: 'off',
                          spellCheck: 'false',
                          'aria-autocomplete': 'none',
                          'data-form-type': 'other',
                          'data-lpignore': 'true',
                        },
                      }}
                    />
                    {trimmedCustomPlanCode ? (
                      <div className="signup-plan-page-custom-action">
                        <UMButton text="Check Custom Plan" onClick={handleCustomPlanAction} disabled={isSubmitting} />
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            )}
            {checkoutError ? (
              <Typography variant="body2" sx={{ color: '#b42318', marginTop: '16px', textAlign: 'center' }}>
                {checkoutError}
              </Typography>
            ) : null}
          </div>
        </div>
      </Animate>

      <Dialog open={Boolean(pendingDowngradePlanName)} onClose={closeDowngradeDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Update Plan</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#4b5563' }}>
            Are you sure you want to update your plan? Updates to a lower tier apply at the end of the current billing
            period.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '0 24px 24px' }}>
          <Button variant="contained" className="modal-no-btn" onClick={closeDowngradeDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" className="modal-yes-btn" onClick={confirmDowngradePlan} disabled={isSubmitting}>
            {isSubmitting ? 'Confirming...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingTrialPlan)}
        onClose={() => !isSubmitting && setPendingTrialPlan(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Try {pendingTrialPlan?.productName} for one month</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#4b5563' }}>
            Your first month costs {formatCurrencyCompact(getTrialPrice(pendingTrialPlan))}{' '}
            {formatPlanCurrency(pendingTrialPlan?.trial?.currency)}. After one month, the corresponding annual{' '}
            {pendingTrialPlan?.productName} plan will be activated automatically at{' '}
            {formatCurrencyCompact(pendingTrialPlan ? getPlanPrice(pendingTrialPlan).totalAnnual : null)}{' '}
            {formatPlanCurrency(pendingTrialPlan?.yearly?.currency)} per year. You can cancel the automatic renewal
            before it takes effect.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '0 24px 24px' }}>
          <Button
            variant="contained"
            className="modal-no-btn"
            onClick={() => setPendingTrialPlan(null)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="contained" className="modal-yes-btn" onClick={confirmTrialPlan} disabled={isSubmitting}>
            {isSubmitting ? 'Confirming...' : 'Start trial'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingCustomPlan)}
        onClose={closeCustomPlanDialog}
        disableRestoreFocus
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              background:
                'var(--signup-plan-card-background, linear-gradient(180deg, rgba(255, 255, 255, 0.78), rgba(255, 255, 255, 0.56)))',
              backdropFilter: 'blur(14px)',
              border: '1px solid rgba(255, 255, 255, 0.24)',
              boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
              borderRadius: '10px',
              width: 'calc(100% - 32px)',
              maxWidth: '430px',
              margin: 0,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: '1.5rem !important',
            lineHeight: 1.05,
            color: 'black',
            padding: '14px 16px 0',
          }}
        >
          {pendingCustomPlan?.plan
            ? pendingCustomPlan.isTrial
              ? `Try ${pendingCustomPlan.plan.productName} for one month`
              : pendingCustomPlan.plan.productName
            : 'Unlocking custom plan'}
        </DialogTitle>
        <DialogContent sx={{ padding: '10px 16px 0 !important' }}>
          {pendingCustomPlan?.isLoading ? (
            <div style={{ minHeight: 360, display: 'grid', placeItems: 'center', gap: 12 }}>
              <CircularProgress size={32} sx={{ color: '#111827' }} />
              <Typography sx={{ color: '#334155' }}>Loading custom plan details...</Typography>
            </div>
          ) : pendingCustomPlan?.plan ? (
            <div style={{ minHeight: 360, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ display: 'grid', gap: 16 }}>
                {pendingCustomPlan.plan.productDescription ? (
                  <Typography sx={{ color: '#334155' }}>{pendingCustomPlan.plan.productDescription}</Typography>
                ) : null}

                <div>
                  <Typography sx={{ fontWeight: 700, color: '#111827', margin: 0, lineHeight: 1.05 }}>
                    <span>
                      {formatCurrencyCompact(
                        pendingCustomPlan.isTrial
                          ? getTrialPrice(pendingCustomPlan.plan)
                          : pendingCustomPlanPrice?.totalAnnual
                      )}
                    </span>{' '}
                    <span style={{ fontWeight: 400 }}>
                      {formatPlanCurrency(
                        pendingCustomPlan.isTrial
                          ? pendingCustomPlan.plan.trial?.currency
                          : pendingCustomPlan.plan.yearly?.currency
                      )}{' '}
                      / {pendingCustomPlan.isTrial ? '1-month trial' : 'year'}
                    </span>
                  </Typography>
                  {pendingCustomPlan.isTrial ? (
                    <Typography variant="body2" sx={{ color: '#334155', marginTop: '6px' }}>
                      Then {formatCurrencyCompact(pendingCustomPlanPrice?.totalAnnual)}{' '}
                      {formatPlanCurrency(pendingCustomPlan.plan.yearly?.currency)} / year
                    </Typography>
                  ) : null}
                </div>

                <div className="signup-plan-page-sensor-row">
                  <strong>{formatLimitValue('volume', getPlanVolumeLimit(pendingCustomPlan.plan))}</strong>
                  <span>m³/max per site</span>
                </div>

                <div className="signup-plan-page-divider" />

                <div style={{ display: 'grid', gap: 10 }}>
                  {getCustomPlanDetails(pendingCustomPlan.plan).map((detail) => (
                    <div
                      key={detail.label}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '72px minmax(0, 1fr)',
                        columnGap: 12,
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#111827', fontWeight: 700, textAlign: 'left' }}>
                        {detail.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#111827', whiteSpace: 'nowrap' }}>
                        {detail.label}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ padding: '16px', paddingTop: '12px' }}>
          <UMButton
            text={
              isSubmitting
                ? 'Confirming...'
                : pendingCustomPlan?.isTrial && !isChangePlan
                  ? 'Start trial'
                  : getCustomPlanActionLabel(pendingCustomPlan?.plan ?? null, isChangePlan, isTrialSubscription)
            }
            fullWidth
            onClick={confirmCustomPlanAction}
            disabled={
              isSubmitting ||
              pendingCustomPlan?.isLoading ||
              !canSubmitCustomPlan(pendingCustomPlan?.plan ?? null, isChangePlan, isTrialSubscription)
            }
            loading={isSubmitting}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}

function getPlanPrice(plan: BillingPlan): PlanPriceSummary {
  return {
    totalAnnual: plan.yearly?.unitAmount == null ? NaN : Number(plan.yearly.unitAmount) / 100,
  };
}

function getTrialPrice(plan: BillingPlan | null) {
  return plan?.trial?.unitAmount == null ? NaN : Number(plan.trial.unitAmount) / 100;
}

function getPlanFeatureRows(plan: BillingPlan) {
  const entitlements = [...(plan.entitlements ?? [])].sort((left, right) => {
    const leftIndex = ENTITLEMENT_ORDER.indexOf(left.lookupKey as (typeof ENTITLEMENT_ORDER)[number]);
    const rightIndex = ENTITLEMENT_ORDER.indexOf(right.lookupKey as (typeof ENTITLEMENT_ORDER)[number]);
    return (
      (leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex) - (rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex)
    );
  });

  return [
    { label: 'm³/year included', value: formatLimitValue('volume', getPlanVolumeLimit(plan)) },
    ...getCustomPlanDetails(plan),
    { label: 'Core Features', value: '✓' },
    ...entitlements.map((entitlement) => ({ label: entitlement.featureName, value: '✓' })),
  ];
}

function formatLimitValue(key: string, value: number | null | undefined) {
  if (value === null || value === undefined) return '—';
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return key === 'volume' && Math.abs(number) >= 1000
    ? `${(number / 1000).toLocaleString('en-CH')}k`
    : number.toLocaleString('en-CH');
}

function getPlanVolumeLimit(plan: BillingPlan) {
  const volume = (plan.limits as Record<string, unknown>)?.volume;
  if (volume === null || volume === undefined) return null;
  const numericVolume = Number(volume);
  return Number.isNaN(numericVolume) ? null : numericVolume;
}

function formatCurrencyCompact(value: number | null | undefined) {
  if (value === null || value === undefined) return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';

  return number.toLocaleString('en-CH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function getCheckoutLookupKey(plan: BillingPlan | null) {
  const lookupKey = plan?.yearly?.lookupKey;
  if (!lookupKey) throw new Error('Missing yearly price lookup key.');
  return lookupKey;
}

function getTrialLookupKey(plan: BillingPlan) {
  const lookupKey = plan.trial?.lookupKey;
  if (!lookupKey) throw new Error('Missing trial price lookup key.');
  return lookupKey;
}

async function createPlanChangeRedirect(
  plan: BillingPlanWithChangeType | null,
  successUrl: string,
  cancelUrl: string,
  source: 'default' | 'custom' = 'default',
  unlockToken?: string,
  forceUpgrade = false
) {
  if (source === 'custom' && unlockToken) {
    if (forceUpgrade || plan?.changeType === 'upgrade') {
      return upgradeCustomSubscription(getCheckoutLookupKey(plan), unlockToken, successUrl, cancelUrl, true);
    }

    if (plan?.changeType === 'downgrade') {
      return null;
    }

    return createCustomPlanCheckout(getCheckoutLookupKey(plan), unlockToken, successUrl, cancelUrl, true);
  }

  if (forceUpgrade || plan?.changeType === 'upgrade') {
    return upgradeSubscription(getCheckoutLookupKey(plan), successUrl, cancelUrl, true);
  }

  if (plan?.changeType === 'downgrade') {
    return null;
  }

  return createCheckout(getCheckoutLookupKey(plan), successUrl, cancelUrl, true);
}

function formatPlanCurrency(currency: string | null | undefined) {
  return typeof currency === 'string' && currency ? currency.toUpperCase() : '—';
}

function formatTrialPlansEnd(date: Date) {
  return date
    .toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/Zurich',
    })
    .replace(',', '');
}

function getCustomPlanActionLabel(
  plan: BillingPlanWithChangeType | null,
  isChangePlan: boolean,
  isTrialSubscription = false
) {
  if (!plan) return 'Subscribe';
  if (!isChangePlan) return 'Subscribe';
  if (isTrialSubscription) return 'Update your plan';
  if (plan.changeType === 'upgrade') return 'Update your plan';
  if (plan.changeType === 'downgrade') return 'Update your plan';
  return 'Current Plan';
}

function canSubmitCustomPlan(
  plan: BillingPlanWithChangeType | null,
  isChangePlan: boolean,
  isTrialSubscription = false
) {
  if (!plan) return false;
  if (!isChangePlan) return true;
  if (isTrialSubscription) return true;
  return plan.changeType !== null && plan.changeType !== undefined;
}

function getCustomPlanDetails(plan: BillingPlan) {
  return [
    {
      label: 'GB of storage',
      value:
        plan.limits.storage_bytes == null ? '—' : (plan.limits.storage_bytes / 1_000_000_000).toLocaleString('en-CH'),
    },
    { label: 'Active Users', value: formatLimitValue('users', plan.limits.users) },
  ];
}

function getExpiredSubscriptionMessage(subscription: BillingSubscription | null) {
  if (!subscription || subscription.status === 'active' || subscription.status === 'incomplete') return '';

  const productName = subscription.planDetails?.productName || 'previous';
  const canceledAt = subscription.canceledAt ? formatSubscriptionDate(subscription.canceledAt) : null;

  if (canceledAt) {
    return `Your ${productName} subscription expired on ${canceledAt}. Please subscribe to a new plan to continue.`;
  }

  return `Your ${productName} subscription has expired. Please subscribe to a new plan to continue.`;
}

function formatSubscriptionDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-CH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
